/**
 * StepForm — primitive O1X de formulaire multi-étapes déclaratif.
 *
 * Intègre nativement dans le runtime O1X le « procédé » du funnel devis
 * (ex-devisFunnel.js transporté) : navigation multi-étapes côté client,
 * validation par étape, champs conditionnels, honeypot et soumission réelle
 * via `/ha/form`. Aucun JS n'est transporté par page — la directive est
 * bindée par le bundle runtime déjà chargé sur toutes les pages.
 *
 * Contrat déclaratif (générique, réutilisable par tout funnel) :
 *
 *   <form data-ha-stepform data-ha-form-id="ha.frm.devis" novalidate>
 *
 *     <!-- Honeypot anti-spam : si rempli, le submit est abandonné en silence -->
 *     <input data-ha-honeypot name="website" tabindex="-1" autocomplete="off">
 *
 *     <!-- Progression (tout optionnel) -->
 *     <div data-ha-step-progress role="progressbar" aria-valuenow="1" …>
 *       <div data-ha-step-bar></div>
 *       <span data-ha-step-dot>1</span> <span data-ha-step-dot>2</span> …
 *     </div>
 *
 *     <!-- Variante : progression HORS du form (ex. en-tête de page). Associer
 *          le conteneur au form par data-ha-stepform-for="<id du form>" ;
 *          l'ordre des pastilles = l'ordre des data-ha-step (HA-14). -->
 *     <div data-ha-step-progress data-ha-stepform-for="wizard-form">…</div>
 *
 *     <section data-ha-step>                     ← étape 1 (visible)
 *       …champs… <span data-ha-error-for="need_type"></span>
 *       <button type="button" data-ha-step-next>Suivant</button>
 *     </section>
 *     <section data-ha-step hidden>              ← étape 2
 *       <div data-ha-reveal-when="sector=other" hidden>…</div>
 *       <button type="button" data-ha-step-prev>Précédent</button>
 *       <button type="button" data-ha-step-next>Suivant</button>
 *     </section>
 *     <section data-ha-step hidden>              ← étape finale
 *       <button type="button" data-ha-step-prev>Précédent</button>
 *       <button type="submit" data-ha-step-submit>Envoyer</button>
 *     </section>
 *   </form>
 *
 * - `data-ha-step-next` valide l'étape courante (champs `required`) avant d'avancer.
 * - `data-ha-step-prev` recule sans validation.
 * - Pastilles : `is-active` = étape courante, `is-done` = étapes passées,
 *   `is-visited` = étape déjà atteinte (cliquable). Cliquer une pastille visitée
 *   y ramène ; avancer valide l'étape courante ; on ne peut jamais sauter vers
 *   une étape non atteinte (HA-14).
 * - Le submit (bouton `type="submit"` / event `submit` du form) valide toutes
 *   les étapes, puis POST `/ha/form` (formId = data-ha-form-id). La réponse
 *   suit le protocole o1x standard (`t:steps`/`app`/`url`/`redirect`/`msg`) et
 *   est routée par le processeur commun (App.processAction) — même chemin que
 *   les formulaires classiques (HA-16). Une réponse NON-navigante porteuse d'un
 *   message (contrat backend `feedback()` : `t:steps` avec un seul step `msg`)
 *   est une erreur métier → affichée dans le form, l'utilisateur reste en place.
 *   Sans navigation ni message, fallback déclaratif `data-ha-redirect`.
 *   La convention forms legacy `{todo:success/error, msg, redirect}` (ex.
 *   CreateDevis) reste honorée : success→redirect/toast, error→erreur inline.
 *   Un succès peut porter `bind` (clé → valeur) : appliqué aux `data-bind*` du
 *   formulaire sans rechargement (HA-274) ; une erreur n'applique rien.
 * - `data-ha-reveal-when="<name>=<v1>|<v2>"` : affiche l'élément quand un contrôle
 *   `[name=<name>]` du form vaut l'une des valeurs (radio/checkbox coché, select
 *   ou champ texte). Une seule valeur reste la forme courante.
 * - `data-ha-require-when="<name>=<v1>|<v2>"` (sur un contrôle) : le contrôle
 *   n'est `required` QUE quand la condition tient — c'est la primitive « champs
 *   dépendants d'un choix » (HA-15) : un bloc révélé par un mode porte ses
 *   required conditionnels, aucun champ n'est requis hors de son mode. Quand la
 *   condition tombe, l'erreur inline éventuelle du champ est effacée.
 * - `data-ha-fill-from="<selectName>"` (sur un champ) : le champ prend sa valeur
 *   de l'option sélectionnée du `<select name=<selectName>>`, lue dans son
 *   attribut `data-<clé>` où clé = `data-ha-fill-key` (défaut : le `name` du
 *   champ). Rempli à l'init SI vide (un brouillon prérempli n'est jamais écrasé)
 *   et à chaque `change` du select (choisir un proche remplit ses champs). Les
 *   options portent les données via `data-bind-attr` côté serveur.
 * - Les erreurs s'affichent dans `[data-ha-error-for="<name>"]` ; le contrôle
 *   reçoit `is-invalid` + `aria-invalid`. Message par champ surchargé via
 *   `data-ha-error-msg` sur le contrôle.
 *
 * - Récapitulatif vivant (HA-18) :
 *   - `data-ha-summary-of="<name>"` : l'élément affiche la valeur COURANTE du
 *     champ — radio coché → son `data-ha-summary-label` (sinon le texte de son
 *     label), checkboxes (`name` exact ou indexé `name[i]…`) → labels cochés
 *     joints par « · », select → texte de l'option, date → format long fr,
 *     texte → valeur. Vide → « — ». Rafraîchi sur `change`/`input`.
 *   - `data-ha-total-of="<radioName>"` / `data-ha-fee-of="<radioName>"` : montant
 *     formaté (fr) depuis le radio coché porteur de `data-ha-price` /
 *     `data-ha-fee` / `data-ha-currency` — les tarifs viennent du serveur
 *     (slot hydraté), jamais du HTML.
 *
 * - Brouillon (HA-19) :
 *   - `data-ha-step-save` (bouton) + `data-ha-save-form-id` (form) : POST de l'état
 *     courant SANS validation (+ `_step` = étape courante) ; réponse o1x (toast).
 *   - `data-ha-prefill-values` (form, posé par le serveur) : JSON plat
 *     `{name: valeur | [valeurs]}` appliqué au bind — un tableau (re)coche exactement
 *     le groupe de checkboxes (les défauts du template sont décochés).
 *   - `data-ha-step-initial` (form) : étape d'ouverture (reprise), étapes
 *     précédentes revisitables.
 *
 * - Pièce jointe (HA-20) :
 *   - `data-ha-file-name="<name>"` : écho de la sélection d'un `input[type=file]`
 *     homonyme (nom + poids, masqué tant que rien n'est choisi) — l'input vit
 *     caché derrière une zone d'upload stylée (`<label for=…>` déclenche le
 *     sélecteur nativement, zéro JS). Le fichier part dans le POST multipart
 *     du submit (FormData brut) ; la validation type/taille est SERVEUR.
 *
 * - `data-ha-prefill-from-query` (sur le form) : au bind, chaque paramètre de
 *   l'URL homonyme d'un contrôle du form le préremplit (radio/checkbox cochés
 *   si la valeur correspond, select si l'option existe, texte assigné), puis un
 *   event `change` est dispatché — reveal-when et styles `:has()` suivent.
 *   Jamais sur honeypot, password ou file. C'est le pont funnel : une page
 *   amont (ex. /forfaits) peut lier `?plan=gold` vers le wizard.
 *
 * Idempotent : marqueur `data-ha-stepform-bound`. Délégation au niveau du form.
 */

import { getAppService, getDataRepository, log } from "../../../../HA";
import { adoptCsrfToken } from "../../Service/ResponseDispatcher";
import { onceI } from "./InteractionController";
import { enqueueSave, formVersion, markFormSaved } from "./FormSave";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function fieldName(control: FieldControl): string {
    // Les multi-valeurs déclarent `name="apps[]"` → on ramène à la clé logique
    // pour cibler `[data-ha-error-for]` et regrouper les radios.
    return (control.name || control.id || "").replace(/\[\]$/, "");
}

function setFieldError(form: HTMLFormElement, name: string, message: string): void {
    const errEl = form.querySelector(`[data-ha-error-for="${name}"]`);
    if (errEl) { errEl.textContent = message; }
    form.querySelectorAll(`[name="${name}"], [name="${name}[]"], #${name}`).forEach((el) => {
        el.classList.add("is-invalid");
        el.setAttribute("aria-invalid", "true");
    });
}

function clearFieldError(form: HTMLFormElement, name: string): void {
    const errEl = form.querySelector(`[data-ha-error-for="${name}"]`);
    if (errEl) { errEl.textContent = ""; }
    form.querySelectorAll(`[name="${name}"], [name="${name}[]"], #${name}`).forEach((el) => {
        el.classList.remove("is-invalid");
        el.removeAttribute("aria-invalid");
    });
}

/**
 * Valide un contrôle requis. Retourne un message d'erreur, ou null si valide.
 * Les contrôles optionnels (non `required`) ne sont validés que sur le format
 * (email/url) lorsqu'ils sont renseignés.
 */
function validateControl(form: HTMLFormElement, control: FieldControl): string | null {
    const name = fieldName(control);
    const required = control.hasAttribute("required");
    const custom = control.getAttribute("data-ha-error-msg");

    if (control.type === "radio") {
        const checked = form.querySelector(`input[name="${control.name}"]:checked`);
        return checked ? null : (custom || "Veuillez faire un choix.");
    }
    if (control.type === "checkbox") {
        if (control instanceof HTMLInputElement && !control.checked) {
            return custom || "Ce champ est requis.";
        }
        return null;
    }

    const value = (control.value || "").trim();
    if (required && value === "") {
        return custom || "Ce champ est requis.";
    }
    if (value !== "" && control.type === "email" && !EMAIL_RE.test(value)) {
        return custom || "Veuillez renseigner une adresse email valide.";
    }
    if (value !== "" && control.type === "url") {
        try { new URL(value); } catch (e) { return custom || "L'URL doit être au format https://…"; }
    }
    return null;
}

/**
 * Valide tous les champs `required` (+ formats) d'un conteneur d'étape.
 * Un seul message par nom logique (évite la répétition sur les radios).
 */
function validateStep(form: HTMLFormElement, step: HTMLElement): boolean {
    const controls = Array.from(
        step.querySelectorAll<FieldControl>("input, select, textarea"),
    );
    let valid = true;
    const seen = new Set<string>();
    controls.forEach((control) => {
        const name = fieldName(control);
        // Un bloc conditionnel masqué (reveal-when) ne participe pas à la
        // validation — ses champs sont hors du mode choisi. (Ne PAS étendre aux
        // steps cachés : au submit, chaque étape est validée step par step.)
        if (control.closest("[data-ha-reveal-when][hidden]")) { return; }
        // Un radio requis dans un groupe suffit à valider le groupe : on ne
        // traite chaque nom qu'une fois, et on ne valide que les requis + formats.
        if (control.type === "radio" && seen.has(name)) { return; }
        const mustCheck = control.hasAttribute("required")
            || (control.value.trim() !== "" && (control.type === "email" || control.type === "url"));
        if (!mustCheck) { return; }
        seen.add(name);
        const error = validateControl(form, control);
        if (error) { setFieldError(form, name, error); valid = false; }
        else { clearFieldError(form, name); }
    });
    return valid;
}

function focusStepHeading(step: HTMLElement): void {
    const heading = step.querySelector<HTMLElement>("[data-ha-step-title], h1, h2");
    if (heading) {
        if (!heading.hasAttribute("tabindex")) { heading.setAttribute("tabindex", "-1"); }
        heading.focus();
    }
}

function showFormError(form: HTMLFormElement, message: string): void {
    let box = form.querySelector<HTMLElement>("[data-ha-form-error]");
    if (!box) {
        box = document.createElement("p");
        box.setAttribute("data-ha-form-error", "");
        box.className = "ha-devis-error ha-devis-submit-error";
        box.setAttribute("role", "alert");
        form.appendChild(box);
    }
    box.textContent = message;
}

/** Lit une clé d'une action o1x, qu'elle soit Map (réponse racine) ou objet nu (step d'un bulk). */
function actionGet(action: any, key: string): any {
    return action instanceof Map ? action.get(key) : action?.[key];
}

/**
 * Aplatit une réponse o1x en liste d'actions : les `steps` d'un `t:steps`/`t:bulk`,
 * sinon la réponse elle-même comme action unique.
 */
function responseActions(response: Map<any, any>): any[] {
    const type = response.get("t") ?? response.get("todo");
    const steps = response.get("steps");
    if ((type === "steps" || type === "bulk") && Array.isArray(steps)) {
        return steps;
    }
    return [response];
}

/** Vocabulaire App.processAction : actions qui quittent ou rechargent la vue courante. */
const NAVIGATION_ACTIONS = new Set([
    "app", "showApp", "toggleApp", "reloadApp", "rc", "reloadCurrent",
    "url", "redirect", "rp", "showModal", "sm",
]);

function hasNavigation(actions: any[]): boolean {
    return actions.some((a) => NAVIGATION_ACTIONS.has(actionGet(a, "t") ?? actionGet(a, "todo")));
}

/** Premier message porté par la réponse (step `msg` : clé `m` ; conventions `msg`/`message`). */
function firstMessage(actions: any[]): string {
    for (const action of actions) {
        const message = actionGet(action, "m") ?? actionGet(action, "msg") ?? actionGet(action, "message");
        if (typeof message === "string" && message !== "") { return message; }
    }
    return "";
}

/**
 * Préremplissage déclaratif depuis la query string (`data-ha-prefill-from-query`).
 * Seuls les contrôles DÉJÀ présents dans le form sont touchés : une valeur
 * d'URL ne peut que sélectionner un choix existant ou remplir un champ texte,
 * comme l'utilisateur l'aurait fait à la main.
 */
/**
 * HA-274 — liaisons renvoyées par un SUCCÈS (`response.bind` : clé → valeur), appliquées aux
 * éléments `data-bind*` du formulaire, sans rechargement : la vue reflète ce que le serveur a
 * enregistré (ex. vignette « Couverture actuelle » après Associer). Jamais sur une erreur.
 */
function applySuccessBindings(form: HTMLFormElement, bind: unknown): void {
    const entries: [string, unknown][] = bind instanceof Map
        ? Array.from(bind.entries()).map(([k, v]) => [String(k), v])
        : (bind && typeof bind === "object" ? Object.entries(bind as Record<string, unknown>) : []);
    for (const [key, raw] of entries) {
        if (raw === null || raw === undefined) { continue; }
        const value = String(raw);
        form.querySelectorAll<HTMLElement>("[data-bind],[data-bind-style],[data-bind-href],[data-bind-value]").forEach((el) => {
            if (el.getAttribute("data-bind") === key) { el.textContent = value; }
            if (el.getAttribute("data-bind-style") === key) { el.setAttribute("style", value); }
            if (el.getAttribute("data-bind-href") === key) { el.setAttribute("href", value); }
            if (el.getAttribute("data-bind-value") === key) { (el as HTMLInputElement).value = value; }
        });
    }
}

function applyPrefillValue(form: HTMLFormElement, name: string, value: string): void {
    const controls = Array.from(
        form.querySelectorAll<FieldControl>(`[name="${CSS.escape(name)}"], [name="${CSS.escape(name)}[]"]`),
    );
    let touched: FieldControl | null = null;
    controls.forEach((control) => {
        if (control.hasAttribute("data-ha-honeypot")) { return; }
        if (control.type === "password" || control.type === "file") { return; }
        if (control.type === "radio" || control.type === "checkbox") {
            if (control.value === value) {
                (control as HTMLInputElement).checked = true;
                touched = control;
            }
        } else if (control instanceof HTMLSelectElement) {
            if (Array.from(control.options).some((option) => option.value === value)) {
                control.value = value;
                touched = control;
            }
        } else {
            control.value = value;
            touched = control;
        }
    });
    if (touched) { touched.dispatchEvent(new Event("change", { bubbles: true })); }
}

/**
 * Valeur GROUPE (tableau) : coche exactement les checkboxes du groupe (`name` exact
 * ou indexé `name[i]…`, mêmes règles que data-ha-summary-of) dont la value est dans
 * la liste — et DÉCOCHE les autres (la reprise d'un brouillon doit effacer les
 * défauts du template).
 */
function applyPrefillGroup(form: HTMLFormElement, name: string, values: string[]): void {
    const esc = CSS.escape(name);
    let touched: FieldControl | null = null;
    form.querySelectorAll<HTMLInputElement>(
        `input[name="${esc}"], input[name="${esc}[]"], input[name^="${esc}["]`,
    ).forEach((control) => {
        if (control.type !== "checkbox" && control.type !== "radio") { return; }
        control.checked = values.includes(control.value);
        touched = control;
    });
    if (touched) { (touched as FieldControl).dispatchEvent(new Event("change", { bubbles: true })); }
}

function prefillFromQuery(form: HTMLFormElement): void {
    const params = new URLSearchParams(window.location.search);
    params.forEach((value, name) => applyPrefillValue(form, name, value));
}

/**
 * Préremplissage serveur (`data-ha-prefill-values`, HA-19) : le contrôleur pose un
 * JSON plat `{name: valeur | [valeurs]}` sur le form (ex. la reprise d'un brouillon)
 * — appliqué comme la saisie manuelle, mêmes garde-fous que le prefill par query.
 */
function prefillFromValues(form: HTMLFormElement): void {
    const raw = form.getAttribute("data-ha-prefill-values");
    if (!raw) { return; }
    let entries: Record<string, unknown>;
    try { entries = JSON.parse(raw); } catch (e) { return; }
    Object.entries(entries).forEach(([name, value]) => {
        if (Array.isArray(value)) {
            applyPrefillGroup(form, name, value.map((v) => String(v)));
        } else if (value !== null && value !== undefined) {
            applyPrefillValue(form, name, String(value));
        }
    });
}

/** Libellé humain d'un contrôle coché : `data-ha-summary-label`, sinon le texte de son label. */
function controlLabel(control: FieldControl): string {
    const explicit = control.getAttribute("data-ha-summary-label");
    if (explicit) { return explicit; }
    const label = control.closest("label");
    return (label?.textContent || control.value || "").replace(/\s+/g, " ").trim();
}

/**
 * Valeur d'AFFICHAGE d'un champ pour le récapitulatif (HA-18) : radio → label du
 * coché, checkboxes → labels cochés joints, select → texte de l'option, date →
 * format long fr, sinon la valeur brute. Les groupes indexés (`services[0][type]`)
 * sont ramenés à leur clé logique.
 */
function displayValue(form: HTMLFormElement, name: string): string {
    const esc = CSS.escape(name);
    const controls = Array.from(form.querySelectorAll<FieldControl>(
        `[name="${esc}"], [name="${esc}[]"], [name^="${esc}["]`,
    ));
    if (controls.length === 0) { return ""; }
    const first = controls[0];
    if (first.type === "radio") {
        const checked = controls.find((c) => (c as HTMLInputElement).checked);
        return checked ? controlLabel(checked) : "";
    }
    if (first.type === "checkbox") {
        return controls
            .filter((c) => (c as HTMLInputElement).checked)
            .map(controlLabel)
            .join(" · ");
    }
    if (first instanceof HTMLSelectElement) {
        const option = first.selectedOptions.item(0);
        return (option?.textContent || first.value || "").replace(/\s+/g, " ").trim();
    }
    if (first.type === "date" && first.value !== "") {
        const date = new Date(`${first.value}T00:00:00`);
        if (!Number.isNaN(date.getTime())) {
            return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
        }
    }
    return (first.value || "").trim();
}

function formatAmount(value: number, currency: string): string {
    return `${new Intl.NumberFormat("fr-FR").format(value)} ${currency}`;
}

function bindStepForm(form: HTMLFormElement): void {
    const steps = Array.from(form.querySelectorAll<HTMLElement>("[data-ha-step]"));
    if (steps.length === 0) { return; }

    // La progression vit dans le form, ou à l'extérieur (en-tête de page)
    // associée par data-ha-stepform-for="<id du form>" (HA-14).
    const progress = form.querySelector<HTMLElement>("[data-ha-step-progress]")
        ?? (form.id
            ? document.querySelector<HTMLElement>(`[data-ha-step-progress][data-ha-stepform-for="${CSS.escape(form.id)}"]`)
            : null);
    const scope = progress ?? form;
    const bar = scope.querySelector<HTMLElement>("[data-ha-step-bar]");
    const dots = Array.from(scope.querySelectorAll<HTMLElement>("[data-ha-step-dot]"));
    let current = 0;
    let maxVisited = 0; // plus haute étape atteinte — borne le retour direct par pastille
    let submitting = false; // garde in-flight : empêche les soumissions dupliquées (double-Enter)

    const show = (index: number): void => {
        current = Math.max(0, Math.min(steps.length - 1, index));
        maxVisited = Math.max(maxVisited, current);
        steps.forEach((step, i) => {
            const active = i === current;
            step.hidden = !active;
            step.classList.toggle("is-active", active);
        });
        if (bar) { bar.style.width = `${Math.round(((current + 1) / steps.length) * 100)}%`; }
        dots.forEach((dot, i) => {
            dot.classList.toggle("is-active", i === current);
            dot.classList.toggle("is-done", i < current);
            dot.classList.toggle("is-visited", i <= maxVisited && i !== current);
        });
        if (progress) { progress.setAttribute("aria-valuenow", String(current + 1)); }
        focusStepHeading(steps[current]);
    };

    // « Enregistrer le brouillon » (HA-19) : POST de l'état courant du form — SANS
    // validation, un brouillon peut être incomplet — vers le form-id déclaré par
    // `data-ha-save-form-id`, avec l'étape courante (`_step`). La réponse suit le
    // protocole o1x (toast non-navigant côté backend).
    let savingDraft = false;
    const saveDraft = (): void => {
        const saveFormId = form.getAttribute("data-ha-save-form-id");
        if (!saveFormId || savingDraft) { return; }
        savingDraft = true;
        const saveButtons = Array.from(form.querySelectorAll<HTMLButtonElement>("[data-ha-step-save]"));
        saveButtons.forEach((b) => { b.disabled = true; });
        const fd = new FormData(form);
        fd.set("formId", saveFormId);
        fd.set("_step", String(current));
        getDataRepository().saveForm(fd).then((response: Map<any, any>) => {
            adoptCsrfToken(response);
            getAppService().processAction(response);
        }).catch(() => {
            showFormError(form, "Impossible d'enregistrer le brouillon. Vérifiez votre connexion et réessayez.");
        }).finally(() => {
            savingDraft = false;
            saveButtons.forEach((b) => { b.disabled = false; });
        });
    };

    // Navigation — délégation au niveau du form (un seul listener).
    form.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        if (!target || !target.closest) { return; }
        if (target.closest("[data-ha-step-next]")) {
            e.preventDefault();
            if (validateStep(form, steps[current])) { show(current + 1); }
        } else if (target.closest("[data-ha-step-prev]")) {
            e.preventDefault();
            show(current - 1);
        } else if (target.closest("[data-ha-step-save]")) {
            e.preventDefault();
            saveDraft();
        }
    });

    // Pastilles cliquables : retour direct vers une étape déjà atteinte.
    // Reculer est libre ; avancer (vers une étape visitée) exige que l'étape
    // courante soit valide ; au-delà de maxVisited, le clic est ignoré.
    if (dots.length > 0) {
        (progress ?? form).addEventListener("click", (e) => {
            const target = e.target as HTMLElement;
            const dot = target && target.closest ? (target.closest("[data-ha-step-dot]") as HTMLElement | null) : null;
            if (!dot) { return; }
            const index = dots.indexOf(dot);
            if (index < 0 || index === current || index > maxVisited) { return; }
            if (index > current && !validateStep(form, steps[current])) { return; }
            show(index);
        });
    }

    // Le préremplissage précède l'évaluation des conditionnels : un champ
    // révélé par la valeur préremplie doit l'être dès le premier rendu.
    // Le prefill serveur (brouillon repris) passe APRÈS la query : la reprise
    // explicite gagne sur un paramètre d'URL résiduel.
    if (form.hasAttribute("data-ha-prefill-from-query")) { prefillFromQuery(form); }
    prefillFromValues(form);

    // Champs conditionnels : data-ha-reveal-when / data-ha-require-when,
    // spec commune "<name>=<v1>|<v2>".
    const matchesSpec = (spec: string): boolean => {
        const eq = spec.indexOf("=");
        if (eq < 0) { return false; }
        const name = spec.slice(0, eq).trim();
        const wanted = spec.slice(eq + 1).split("|").map((v) => v.trim()).filter((v) => v !== "");
        let match = false;
        form.querySelectorAll<FieldControl>(`[name="${name}"], [name="${name}[]"]`).forEach((c) => {
            if (c.type === "checkbox" || c.type === "radio") {
                if ((c as HTMLInputElement).checked && wanted.includes(c.value)) { match = true; }
            } else if (wanted.includes(c.value)) { match = true; }
        });
        return match;
    };
    const conditionals = Array.from(form.querySelectorAll<HTMLElement>("[data-ha-reveal-when]"));
    const requireWhen = Array.from(form.querySelectorAll<FieldControl>("[data-ha-require-when]"));
    if (conditionals.length > 0 || requireWhen.length > 0) {
        const evaluate = (): void => {
            conditionals.forEach((el) => {
                const shown = matchesSpec(el.getAttribute("data-ha-reveal-when") || "");
                el.hidden = !shown;
                // Un conteneur avec une règle d'affichage propre (`.field-row{display:grid}`,
                // `.field{…}`) l'emporte sur l'UA `[hidden]{display:none}` (même origine
                // auteur) → le bloc « masqué » resterait VISIBLE. On force donc l'`display`
                // inline (bat toute règle de classe) ; `''` rend la main à la feuille quand affiché.
                el.style.display = shown ? "" : "none";
            });
            requireWhen.forEach((control) => {
                const required = matchesSpec(control.getAttribute("data-ha-require-when") || "");
                if (required) { control.setAttribute("required", ""); }
                else {
                    control.removeAttribute("required");
                    clearFieldError(form, fieldName(control));
                }
            });
        };
        form.addEventListener("change", evaluate);
        evaluate();
    }

    // Auto-remplissage depuis un <select> : un champ `data-ha-fill-from="<selectName>"`
    // recopie, depuis l'option sélectionnée, l'attribut `data-<clé>` (clé =
    // `data-ha-fill-key`, défaut = son `name`). À l'init on ne remplit que les champs
    // VIDES (un brouillon prérempli est préservé) ; à chaque `change` du select on
    // remplit (choisir un proche renseigne ses champs). Un `input` est redispatché
    // pour que récap/reveal suivent.
    const fillTargets = Array.from(form.querySelectorAll<FieldControl>("[data-ha-fill-from]"));
    if (fillTargets.length > 0) {
        const bySelect = new Map<string, FieldControl[]>();
        fillTargets.forEach((target) => {
            const src = target.getAttribute("data-ha-fill-from") || "";
            if (src === "") { return; }
            const list = bySelect.get(src) ?? [];
            list.push(target);
            bySelect.set(src, list);
        });
        const applyFill = (selectName: string, onlyEmpty: boolean): void => {
            const select = form.querySelector<HTMLSelectElement>(`select[name="${CSS.escape(selectName)}"]`);
            const option = select?.selectedOptions.item(0);
            if (!option) { return; }
            (bySelect.get(selectName) || []).forEach((field) => {
                const key = field.getAttribute("data-ha-fill-key") || fieldName(field);
                const value = option.getAttribute(`data-${key}`);
                if (value === null) { return; }
                if (onlyEmpty && (field as HTMLInputElement).value.trim() !== "") { return; }
                (field as HTMLInputElement).value = value;
                field.dispatchEvent(new Event("input", { bubbles: true }));
            });
        };
        bySelect.forEach((_targets, selectName) => {
            const select = form.querySelector<HTMLSelectElement>(`select[name="${CSS.escape(selectName)}"]`);
            if (!select) { return; }
            select.addEventListener("change", () => applyFill(selectName, false));
            applyFill(selectName, true);
        });
    }

    // Récapitulatif vivant (HA-18) : miroirs de champs + montants du forfait coché.
    const summaries = Array.from(form.querySelectorAll<HTMLElement>("[data-ha-summary-of]"));
    const amounts = Array.from(form.querySelectorAll<HTMLElement>("[data-ha-total-of], [data-ha-fee-of]"));
    if (summaries.length > 0 || amounts.length > 0) {
        const refreshSummaries = (): void => {
            summaries.forEach((el) => {
                el.textContent = displayValue(form, el.getAttribute("data-ha-summary-of") || "") || "—";
            });
            amounts.forEach((el) => {
                const name = el.getAttribute("data-ha-total-of") ?? el.getAttribute("data-ha-fee-of") ?? "";
                const source = form.querySelector<HTMLInputElement>(`input[name="${CSS.escape(name)}"]:checked`);
                if (!source) { el.textContent = "—"; return; }
                const price = Number(source.getAttribute("data-ha-price") || "0");
                const fee = Number(source.getAttribute("data-ha-fee") || "0");
                const currency = source.getAttribute("data-ha-currency") || "FCFA";
                el.textContent = formatAmount(el.hasAttribute("data-ha-total-of") ? price + fee : fee, currency);
            });
        };
        form.addEventListener("change", refreshSummaries);
        form.addEventListener("input", refreshSummaries);
        refreshSummaries();
    }

    // Champ fichier (HA-20) : la sélection se reflète dans [data-ha-file-name="<name>"]
    // (nom + poids) — l'input est masqué derrière la zone d'upload, sans écho l'utilisateur
    // ne sait pas que son document est bien à bord.
    form.addEventListener("change", (e) => {
        const input = e.target as HTMLInputElement;
        if (!input || input.type !== "file" || !input.name) { return; }
        const echo = form.querySelector<HTMLElement>(`[data-ha-file-name="${CSS.escape(input.name)}"]`);
        if (!echo) { return; }
        const file = input.files && input.files[0];
        echo.textContent = file ? `📎 ${file.name} — ${(file.size / 1048576).toFixed(1)} Mo` : "";
        echo.hidden = !file;
    });

    // Validation inline au blur (retire l'erreur dès que le champ redevient valide).
    form.addEventListener("blur", (e) => {
        const control = e.target as FieldControl;
        if (!control || !("value" in control)) { return; }
        const name = fieldName(control);
        if (!form.querySelector(`[data-ha-error-for="${name}"]`)) { return; }
        const error = validateControl(form, control);
        if (error) { setFieldError(form, name, error); }
        else { clearFieldError(form, name); }
    }, true);

    // Soumission finale.
    form.addEventListener("submit", (e) => {
        e.preventDefault();

        if (submitting) { return; } // soumission déjà en vol (double-Enter, second event submit)

        const honeypot = form.querySelector<HTMLInputElement>("[data-ha-honeypot]");
        if (honeypot && honeypot.value.trim() !== "") { return; } // spam : abandon silencieux

        // Validation en une passe : premier step invalide → on s'y positionne.
        const firstInvalid = steps.findIndex((step) => !validateStep(form, step));
        if (firstInvalid >= 0) { show(firstInvalid); return; }

        submitting = true;
        const errorBox = form.querySelector<HTMLElement>("[data-ha-form-error]");
        if (errorBox) { errorBox.textContent = ""; } // l'erreur du précédent essai ne survit pas au retry
        const submitBtn = form.querySelector<HTMLButtonElement>("[data-ha-step-submit]");
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add("is-loading"); }

        const fd = new FormData(form);
        const formId = form.getAttribute("data-ha-form-id");
        if (formId) { fd.set("formId", formId); }

        const done = (): void => {
            submitting = false;
            if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove("is-loading"); }
        };

        // HA-276 : même file que l'autosave et le vidage de sortie ; seule la version soumise
        // est déclarée sauvegardée, et seulement sur succès.
        const version = formVersion(form);
        enqueueSave(form, () => getDataRepository().saveForm(fd)).then((response: Map<any, any>) => {
            adoptCsrfToken(response);
            const type = response.get("t") ?? response.get("todo");

            // Convention forms HorApp `{todo:success/error, msg, redirect}`
            // (ex. CreateDevis) — distincte du protocole d'actions o1x.
            if (type === "error") {
                done();
                showFormError(form, response.get("msg") || "Erreur lors de l'envoi. Veuillez réessayer.");
                return;
            }
            if (type === "success" || response.get("ok") === true || response.get("success") === true) {
                markFormSaved(form, version);
                const redirect = response.get("redirect") || form.getAttribute("data-ha-redirect");
                if (redirect) { window.location.href = redirect; return; }
                if (response.has("bind")) { applySuccessBindings(form, response.get("bind")); }
                if (response.get("msg")) { log(response.get("msg")); }
                done();
                return;
            }

            const actions = responseActions(response);

            // Réponse navigante (succès) : router via le processeur o1x standard
            // — même chemin que les formulaires classiques (steps→bulk, app→SPA,
            // url/redirect→navigation, msg→toast). Le bouton submit RESTE
            // désactivé : la vue part, le réactiver rouvrirait le double-submit.
            if (hasNavigation(actions)) {
                markFormSaved(form, version);
                getAppService().processAction(response);
                return;
            }

            // Réponse o1x non-navigante = feedback d'erreur métier (contrat
            // backend `feedback()` : t:steps avec un seul step msg) → rester
            // sur le form et surfacer le message.
            done();
            const message = firstMessage(actions);
            if (message) {
                showFormError(form, message);
                return;
            }

            // Ni navigation ni message : fallback déclaratif.
            const redirect = response.get("redirect") || form.getAttribute("data-ha-redirect");
            if (redirect) { window.location.href = redirect; }
        }).catch(() => {
            done();
            showFormError(form, "Impossible d'envoyer la demande. Vérifiez votre connexion et réessayez.");
        });
    });

    // Étape d'ouverture : `data-ha-step-initial` (posé par le serveur à la reprise
    // d'un brouillon, HA-19) — les étapes précédentes deviennent revisitables.
    const initial = parseInt(form.getAttribute("data-ha-step-initial") || "0", 10);
    show(Number.isNaN(initial) ? 0 : initial);
}

export function initStepForms(root: ParentNode = document): void {
    const forms = root.querySelectorAll<HTMLFormElement>("[data-ha-stepform]:not([data-ha-stepform-bound])");
    forms.forEach((form) => {
        if (!onceI(form, "data-ha-stepform-bound")) { return; }
        bindStepForm(form);
    });
}
